import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { shortDay } from "@/lib/tina/festival-page";
import type { FestivalPageRead } from "@/types/tina-festival-page";

const FIRST_ROWS = 50;

interface ProgramBodyProps {
  page: FestivalPageRead;
  /**
   * `group`: the group waits for Approve group. `live`: the group exists. `read_only`: nothing can be done with it, so
   * no note promises a group.
   */
  variant: "group" | "live" | "read_only";
}

const inMoodLabel = (visibility: string | null): string =>
  visibility === "draft"
    ? "Draft"
    : visibility === "hidden"
      ? "Hidden"
      : "Live";

/** A program festival's concerts, as text: nothing to edit here, the group is made from them as they are. */
export function ProgramBody({ page, variant }: ProgramBodyProps) {
  const [all, setAll] = useState(false);
  const concerts = [...page.concerts].sort((a, b) =>
    (a.service_day ?? "") === (b.service_day ?? "")
      ? a.name.localeCompare(b.name)
      : (a.service_day ?? "") < (b.service_day ?? "")
        ? -1
        : 1,
  );
  const shown = all ? concerts : concerts.slice(0, FIRST_ROWS);

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <h2 className="text-lg font-semibold">Concerts</h2>
      <p className="text-sm text-muted-foreground">
        {variant === "live" ? (
          "The group is live in Mood. A concert Tina finds later joins it by itself."
        ) : variant === "read_only" ? (
          "No editing here."
        ) : (
          <>
            No editing here. Approve makes the group <b>{page.name}</b> with all{" "}
            {concerts.length} concerts. A concert Tina finds later joins the
            group by itself.
          </>
        )}
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Concert</TableHead>
            <TableHead>Venue</TableHead>
            <TableHead>In Mood</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {shown.map((concert) => (
            <TableRow key={concert.mood_event_id}>
              <TableCell>
                {concert.service_day ? shortDay(concert.service_day) : "—"}
              </TableCell>
              <TableCell>
                <a
                  href={`/events/edit/${concert.mood_event_id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  {concert.name}
                </a>
              </TableCell>
              <TableCell>{concert.venue_name ?? "—"}</TableCell>
              <TableCell className="flex gap-1">
                <Badge variant="outline" className="font-normal">
                  {inMoodLabel(concert.visibility)}
                </Badge>
                {concert.in_group ? (
                  <Badge variant="secondary" className="font-normal">
                    In the group
                  </Badge>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!all && concerts.length > FIRST_ROWS ? (
        <Button
          type="button"
          variant="ghost"
          className="self-start"
          onClick={() => setAll(true)}
        >
          Show all {concerts.length}
        </Button>
      ) : null}
    </section>
  );
}
